/**
 * Top-centre extraction timer (02 §10): `OSPREY 0:45` while inbound, then `LADDER 0:20` while
 * it holds, with a chevron and distance to the pickup X.
 */

import './ospreyTimer.css';
import { TICK_RATE } from '../../sim/types';
import type { MissionDef, World } from '../../sim/types';
import { el, mmss, show, Shown } from './dom';
import { radioSilent } from './missionConditions';

export class OspreyTimer {
  readonly el: HTMLElement;
  private readonly label: HTMLElement;
  private readonly time: HTMLElement;
  private readonly chevronBox: HTMLElement;
  private readonly chevron: HTMLElement;
  private readonly dist: HTMLElement;
  private readonly shown = new Shown();

  constructor(private readonly def: MissionDef) {
    this.el = el('div', 'hud-osprey hud-hidden');
    this.label = el('span', 'hud-osprey-label', 'OSPREY');
    this.time = el('span', 'hud-osprey-time');
    this.chevronBox = el('span', 'hud-chevron-box');
    this.chevron = el('span', 'hud-chevron');
    this.chevronBox.append(this.chevron);
    this.dist = el('span', 'hud-osprey-dist');
    if (radioSilent(def)) this.el.classList.add('hud-osprey-silent');
    this.el.append(this.label, this.time, this.chevronBox, this.dist);
  }

  update(world: World): void {
    const shown = this.shown;
    const phase = world.osprey.phase;
    const mode = phase === 'inbound' ? 1 : phase === 'hovering' ? 2 : 0;
    if (shown.changed('mode', mode)) {
      show(this.el, mode !== 0);
      this.el.classList.toggle('hud-osprey-hold', mode === 2);
      this.label.textContent = mode === 2 ? 'LADDER' : 'OSPREY';
    }
    if (mode === 0) return;
    const seconds = Math.max(0, Math.ceil(world.osprey.ticks / TICK_RATE));
    if (shown.changed('time', seconds)) this.time.textContent = mmss(seconds);

    // The camera is fixed north-up, so a map bearing is also a screen direction.
    //
    // ROUND5: this read `def.pickup` - where the mission ORIGINALLY put the X - so on any mission that
    // moves it the needle and the range pointed at ground the Osprey was not coming to. Mission 4 moves
    // it to wherever the officer was taken (`relocatesToCapture`) and Alternate LZ now has the aircraft
    // choose between two points. `world.objective` is where it is actually going; that is what to draw.
    const dx = world.objective.pickupX - world.ranger.x;
    const dz = world.objective.pickupZ - world.ranger.z;
    const metres = Math.round(Math.hypot(dx, dz));
    const near = metres < 3;
    /*
     * Radio Silence (04 §7) says "no map and no MARKERS". The chevron is a marker - a needle that walks him
     * to the X without his ever reading the ground - so it goes, and finding the pickup stays the navigation
     * problem the modifier is for. The RANGE is not a marker: it is one number, it points nowhere, and
     * without it "no map" turns a 40-second walk into a hunt with the Osprey burning its hover. He knows
     * where the X is from the briefing; the modifier should cost him the line to it, not the fact of it.
     */
    if (radioSilent(this.def)) {
      if (shown.changed('near', near)) show(this.chevronBox, false);
      if (shown.changed('dist', near ? -1 : metres)) this.dist.textContent = near ? 'AT THE X' : `${metres}M`;
      return;
    }
    if (shown.changed('near', near)) show(this.chevronBox, !near);
    if (shown.changed('dist', near ? -1 : metres)) this.dist.textContent = near ? 'AT THE X' : `${metres}M`;
    if (near) return;
    const sixteenth = (Math.round(Math.atan2(dx, -dz) / (Math.PI / 8)) + 16) % 16;
    if (shown.changed('bearing', sixteenth)) this.chevron.style.transform = `rotate(${sixteenth * 22.5}deg)`;
  }
}
